import { SportConfigExport } from './';
import {
	$t,
	singleStepOverview,
	singleStepCompetition,
	singleStepEvent,
	singleStepTeam,
	getCompetitorsParams,
	CompetitorComposition,
} from './singleStepContexts';

import { SportPageType } from '@/interfaces/sport-graphql';
import { SingleStepSportCompetitorsV2, SingleStepSportEvent, SportType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';

/* CONSTANTS */
const sportName = $t('SPORTS_WEBAPP_SPORT_TYPE_TENNIS');
const switchedLocales = [WebLocale.us, WebLocale.ca];

function shortName(name: string) {
	const firstNameInital = name.charAt(0);
	const surname = name.split(' ').slice(1).join(' ');

	return `${surname} ${firstNameInital}`;
}

function isDoubles({ homeTeam, awayTeam }: SingleStepSportCompetitorsV2) {
	return homeTeam.competitors.length === 2 && awayTeam.competitors.length === 2;
}

function getCompetitors(event: SingleStepSportEvent) {
	if (!isDoubles(event.competitors)) {
		return getCompetitorsParams(event, false, CompetitorComposition.Player);
	}

	const { homeTeam, awayTeam } = event.competitors;

	return {
		start: homeTeam.competitors.map(t => ({
			name: shortName(t.content.name),
			iconUrl: t.content.iconUrl!,
			score: homeTeam.score!,
			fullPath: t.content.fullPath ?? undefined,
			sportCategory: CompetitorComposition.Player,
		})),
		end: awayTeam.competitors.map(t => ({
			name: shortName(t.content.name),
			iconUrl: t.content.iconUrl!,
			score: awayTeam.score!,
			fullPath: t.content.fullPath ?? undefined,
			sportCategory: CompetitorComposition.Player,
		})),
		isSwitched: false,
	};
}

function getEventTitle(event: SingleStepSportEvent) {
	if (!isDoubles(event.competitors)) {
		const homeTeam = event.competitors.homeTeam.competitors[0].content.name;
		const awayTeam = event.competitors.awayTeam.competitors[0].content.name;

		return { key: $t('SPORTS_WEBAPP_TEAM_VS'), params: { homeTeam, awayTeam } };
	}

	const { start, end } = getCompetitors(event);
	const homeTeam = start.map(team => team.name).join(' / ');
	const awayTeam = end.map(team => team.name).join(' / ');

	return { key: $t('SPORTS_WEBAPP_TEAM_VS'), params: { homeTeam, awayTeam } };
}

/* CONTEXTS */
function overview() {
	const { getEventTitleParams, ...rest } = singleStepOverview(SportType.Tennis);

	function getEventTitle(event: SingleStepSportEvent, webLocale: WebLocale) {
		return getEventTitleParams(event, switchedLocales.includes(webLocale));
	}

	return {
		sportName,
		getEventTitle,
		headline: $t('SPORTS_WEBAPP_OVERVIEW_HEADLINE_TENNIS'),
		subheading: $t('SPORTS_WEBAPP_OVERVIEW_TEXT_TENNIS'),
		getCompetitors,
		...singleStepOverview(SportType.Tennis),
		...rest,
	};
}

function competition() {
	const { getEventTitleParams, ...rest } = singleStepCompetition(SportType.Tennis);

	function getEventTitle(event: SingleStepSportEvent, webLocale: WebLocale) {
		return getEventTitleParams(event, switchedLocales.includes(webLocale));
	}

	return {
		sportName,
		getEventTitle,
		getCompetitors,
		...singleStepCompetition(SportType.Tennis),
		...rest,
	} as const;
}

function event() {
	const { getEventTitleParams, ...rest } = singleStepEvent(SportType.Tennis);

	return {
		sportName,
		getEventTitle,
		getCompetitors,
		...rest,
	};
}

function team() {
	const { getEventTitleParams, ...rest } = singleStepTeam(SportType.Tennis);

	return {
		sportName,
		getEventTitle,
		getCompetitors,
		...rest,
	};
}

/* EXPORTS */
const config: SportConfigExport = {
	[SportPageType.SportOverview]: overview,
	[SportPageType.SportCompetition]: competition,
	[SportPageType.SportEvent]: event,
	[SportPageType.SportTeam]: team,
};

export default config;
